import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subscription } from 'rxjs';
import { MediaplanCheckService } from '~services/mediaplan-check.service';
import { ICheckStatus, IStatusItem } from '~models/interfaces/check-status.interface';

@Component({
	selector: 'led-mediaplan-status-dialog',
	standalone: true,
	imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatListModule, MatProgressSpinnerModule],
	templateUrl: './mediaplan-status-dialog.component.html',
	styleUrl: './mediaplan-status-dialog.component.scss',
})
export class MediaplanStatusDialogComponent implements OnInit, OnDestroy {
	statusItems: IStatusItem[] = [];
	isUpdating = true;
	updateCount = 0;

	private statusSubscription?: Subscription;
	private statusMap = new Map<string, IStatusItem>();

	constructor(
		private dialogRef: MatDialogRef<MediaplanStatusDialogComponent>,
		private mediaplanCheckService: MediaplanCheckService,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {}

	public ngOnInit(): void {
		this.startUpdates();
	}

	public ngOnDestroy(): void {
		this.stopUpdates();
	}

	public hasAction(): boolean {
		return !!this.statusItems.find((item) => item.status === 'Waiting');
	}

	private startUpdates(): void {
		if (this.isUpdating && !this.statusSubscription) {
			this.statusSubscription = this.mediaplanCheckService.getActualStatuses().subscribe({
				next: (status: ICheckStatus) => {
					this.updateStatus(status);
					this.updateCount++;
				},
				error: (error) => {
					console.error('Error receiving statuses:', error);
				},
			});
		}
	}

	private stopUpdates(): void {
		if (this.statusSubscription) {
			this.statusSubscription.unsubscribe();
			this.statusSubscription = undefined;
		}
	}

	private updateStatus(newStatus: ICheckStatus): void {
		const statusItem: IStatusItem = {
			...newStatus,
			icon: this.getStatusIcon(newStatus.status),
			color: this.getStatusColor(newStatus.status),
			receivedAt: new Date(),
		};

		// Обновляем или добавляем статус
		this.statusMap.set(newStatus.uuid, statusItem);

		// Преобразуем Map обратно в массив для отображения
		this.statusItems = Array.from(this.statusMap.values());

		// Сортируем по времени получения (новые сверху)
		// this.statusItems.sort((a, b) => b.receivedAt.getTime() - a.receivedAt.getTime());
	}

	private getStatusIcon(status: string): string {
		switch (status) {
			case 'OK':
				return 'check_circle';
			case 'Error':
				return 'error';
			case 'Waiting':
				return 'schedule';
			default:
				return 'help';
		}
	}

	private getStatusColor(status: string): string {
		switch (status) {
			case 'OK':
				return '#4caf50';
			case 'Error':
				return '#f44336';
			case 'Waiting':
				return '#ff9800';
			default:
				return '#9e9e9e';
		}
	}

	getStatusText(status: string): string {
		switch (status) {
			case 'OK':
				return 'Готово';
			case 'Error':
				return 'Ошибка';
			case 'Waiting':
				return 'Ожидание';
			default:
				return status;
		}
	}

	toggleUpdates(): void {
		this.isUpdating = !this.isUpdating;

		if (this.isUpdating) {
			this.startUpdates();
		} else {
			this.stopUpdates();
		}
	}

	onClose(): void {
		this.dialogRef.close({
			totalUpdates: this.updateCount,
			itemsCount: this.statusItems.length,
		});
	}
}
